"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/button";
import { InfoCard } from "@/components/info-card";
import { Input } from "@/components/input";
import { CmwLogo } from "@/components/logo";
import { useSecondsUntil } from "@/hooks/use-seconds-until";
import { getApiErrorMessage, getApiErrorStatus } from "@/lib/ky/error-message";
import { formatCountdown } from "@/lib/format-countdown";
import { useSignInCodeRetryStore } from "@/lib/otp-retry-store";
import { routes } from "@/lib/routes";
import { CODE_LENGTH, CodeInput } from "../../forms/code-input";
import { useRequestSignInCode } from "../../hooks/use-request-sign-in-code";
import { useSignInWithCode } from "../../hooks/use-sign-in-with-code";

export type SignInWithCodePhase = "request-code" | "verify";

type SignInWithCodeFormProps = {
  /** Prefilled from `?email=` on the migration link, normalised server-side by the page. */
  initialEmail: string;
};

export function SignInWithCodeForm({ initialEmail }: SignInWithCodeFormProps) {
  const [phase, setPhase] = useState<SignInWithCodePhase>("request-code");
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [justResent, setJustResent] = useState(false);

  const requestCode = useRequestSignInCode();
  const verifyCode = useSignInWithCode();

  const retryAt = useSignInCodeRetryStore((state) => state.retryAt);
  const setRetryAt = useSignInCodeRetryStore((state) => state.setRetryAt);
  const secondsUntilRetry = useSecondsUntil(retryAt);
  const canRequestCode = secondsUntilRetry <= 0;

  // `skipHydration: true` on the store means every consumer must rehydrate itself in an effect,
  // or `retryAt` is always null on first render — see .spec/context/gotchas.md §4.
  useEffect(() => {
    useSignInCodeRetryStore.persist.rehydrate();
  }, []);

  /**
   * Shared by the phase-1 submit and the phase-2 resend. Deliberately not fired on mount — a mail
   * gateway's link-scanner (Outlook SafeLinks, Proofpoint) would otherwise mint the code before
   * the human ever clicks. See .spec/passwordless-sign-in.md §8.2.
   */
  function sendCode() {
    setError(null);
    setFieldError(null);

    const wasAlreadyVerifying = phase === "verify";

    requestCode.mutate(email, {
      onSuccess: (data) => {
        setCode("");
        setRetryAt(data.retryAt ?? null);
        setPhase("verify");
        setJustResent(wasAlreadyVerifying);
      },
      onError: async (cause) => {
        // 400/422 is class-validator rejecting the email shape — everything else is a real
        // failure. Read the body at most once; see .spec/context/gotchas.md §3.
        const status = getApiErrorStatus(cause);

        if (status === 400 || status === 422) {
          setFieldError(await getApiErrorMessage(cause, "Please provide a valid email address"));
          return;
        }

        setError(await getApiErrorMessage(cause, "Could not send the code. Please try again."));
      },
    });
  }

  function onRequest(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    sendCode();
  }

  function onResend() {
    sendCode();
  }

  function onVerify(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    verifyCode.mutate(
      { email, code },
      {
        onError: async (cause) => {
          // Kept, not cleared: it's a single input, so they can fix one digit rather than
          // retyping the whole code.
          setError(
            await getApiErrorMessage(
              cause,
              "That code didn't work. It may have expired, or it may have been sent to a different address.",
            ),
          );
        },
      },
    );
  }

  function useDifferentAddress() {
    setPhase("request-code");
    setEmail("");
    setCode("");
    setError(null);
    setFieldError(null);
    setJustResent(false);
    setRetryAt(null);
  }

  return (
    <div className="flex w-full max-w-438 flex-col items-center gap-24 py-64 lg:py-12">
      <div className="flex w-full flex-col items-start gap-8">
        <Link
          href={routes.signIn.url}
          aria-label="Back to sign in"
          className="flex size-32 items-center justify-center text-brand-dark-blue"
        >
          <ArrowLeftIcon className="size-32" />
        </Link>
      </div>
      <CmwLogo type="vertical" className="hidden lg:block" />

      <div className="flex flex-col items-center gap-8 text-center">
        <h1 className="font-sans text-32 font-semibold leading-[1.3] text-neutral-black">Sign in with a code</h1>
        <p className="text-16 leading-[1.7] text-neutral-black/70">
          {phase === "request-code" ? (
            "Enter your email address and we'll send you a one-time sign-in code."
          ) : (
            <>
              {"If an account exists for "}
              <span className="font-semibold leading-[1.4] text-brand-dark-blue">{email}</span>
              {", a sign-in code is on its way. Enter it below."}
            </>
          )}
        </p>
      </div>

      {phase === "request-code" && (
        <form onSubmit={onRequest} className="flex w-full flex-col gap-24">
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            autoFocus
            label="Email Address"
            placeholder="you@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            state={fieldError ? "error" : "default"}
            message={fieldError ?? undefined}
          />

          {error && (
            <div role="alert">
              <InfoCard title="Could not send the code" description={error} />
            </div>
          )}

          <Button
            type="submit"
            variant="primary-blue"
            size="l"
            aria-disabled={requestCode.isPending || !canRequestCode}
            onClick={(event) => {
              if (requestCode.isPending || !canRequestCode) {
                event.preventDefault();
              }
            }}
            className="w-full aria-disabled:pointer-events-none aria-disabled:opacity-30"
          >
            {requestCode.isPending ? (
              <ArrowPathIcon className="size-20 animate-spin" />
            ) : canRequestCode ? (
              "Send Code"
            ) : (
              `Try again in ${formatCountdown(secondsUntilRetry)}`
            )}
          </Button>
        </form>
      )}

      {phase === "verify" && (
        <>
          <form onSubmit={onVerify} className="flex w-full flex-col items-center gap-24">
            <CodeInput value={code} onChange={setCode} autoFocus />

            {error && (
              <div role="alert" className="w-full">
                <InfoCard title="That didn't work" description={error} />
              </div>
            )}

            <Button
              type="submit"
              variant="primary-blue"
              size="l"
              disabled={code.length < CODE_LENGTH || verifyCode.isPending}
              className="w-full"
            >
              {verifyCode.isPending ? <ArrowPathIcon className="size-20 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          <div className="flex flex-col items-center gap-8">
            <p className="text-16 leading-[1.7] text-neutral-black/70">
              {"Didn't receive the code? "}
              {/*
               * `aria-disabled` + an early-return click handler, not `disabled`: a disabled button
               * is unfocusable and a screen reader never hears why resend does nothing, unlike
               * verify-email-form.tsx, which uses `disabled` for the equivalent button.
               */}
              <button
                type="button"
                aria-disabled={requestCode.isPending || !canRequestCode}
                onClick={() => {
                  if (requestCode.isPending || !canRequestCode) return;
                  onResend();
                }}
                className="cursor-pointer font-medium leading-[1.4] text-brand-dark-blue aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              >
                {requestCode.isPending
                  ? "Sending…"
                  : canRequestCode
                    ? "Resend"
                    : `Resend in ${formatCountdown(secondsUntilRetry)}`}
              </button>
            </p>

            {justResent && (
              <p role="status" className="text-14 leading-[1.4] text-neutral-black/70">
                A code is on its way.
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={useDifferentAddress}
            className="cursor-pointer text-16 font-medium leading-[1.4] text-brand-dark-blue"
          >
            Wrong email address? Use a different address
          </button>
        </>
      )}

      <p className="text-16 leading-[1.7] text-neutral-black/70">
        {"Prefer your password? "}
        <Link href={routes.signIn.url} className="font-semibold text-brand-dark-blue">
          Sign in
        </Link>
      </p>
    </div>
  );
}
